import type { FeatureAttribute, FeatureFlags } from '@magicschool/business-logic';
import { type ClientSiteConfigs, defaultClientSiteConfigs } from '@magicschool/business-logic/site-configs';
import type { Tables } from '@magicschool/supabase/types';
import type { MagicSchoolUser, SessionClaimsUser } from '@magicschool/supabase/user';
import type { StudentUser } from '@/app/_actions/students/jwt/decodeStudentJWT';
import { type AuthFormStore, createAuthFormStoreSlice } from '@/app/(public)/(auth)/auth/authFormStore';
import { createJoinStoreSlice, type JoinStore } from '@/app/(student)/(public)/s/join/joinStore';
import { createStudentStoreSlice, type StudentStore } from '@/app/(student)/s/(protected)/studentStore';
import { createOrgDashboardStoreSlice, type OrgDashboardStore } from '@/app/(teacher)/(metabase)/orgDashboardStore';
import { createToolListStoreSlice, type ToolListStore } from '@/app/(teacher)/admin/(admin-access)/tools/toolListStore';
import { type AdminOrgsStore, createAdminOrgsStoreSlice } from '@/app/(teacher)/admin/orgs/adminOrgsStore';
import { type AdminUsersStore, createAdminUsersStoreSlice } from '@/app/(teacher)/admin/users/adminUsersStore';
import { createRoomDetailsStoreSlice, type RoomDetailsStore } from '@/app/(teacher)/magic-student/rooms/[id]/roomDetailsStore';
import { createRoomJoinStoreSlice, type RoomCoTeacherStore } from '@/app/(teacher)/magic-student/rooms/coTeacher/roomCoTeacherStore';
import { createOrgRoomsStoreSlice, type OrgRoomsStore } from '@/app/(teacher)/o/rooms/orgRoomsStore';
import { createToolCustomizationStoreSlice, type ToolCustomizationStore } from '@/components/CustomizationDrawer/toolCustomizationStore';
import { createTeacherProfileStoreSlice, type TeacherProfileStore } from '@/components/ProfileBuilder/TeacherProfileStore';
import { createToolAccessStoreSlice, type ToolAccessStore } from '@/features/admin/tools/access/toolAccessStore';
import { createEditToolStoreSlice, type EditToolStore } from '@/features/admin/tools/editToolStore';
import { type AuthStore, createAuthStoreSlice, emptyUser } from '@/features/auth/authStore';
import { type ChatStore, createChatStoreSlice } from '@/features/model-interaction/chatStore';
import { createNotificationStoreSlice, type NotificationStore } from '@/features/notifications/notificationsStore';
import { createModerationSettingsStoreSlice, type ModerationSettingsStore } from '@/features/org-admin-moderations/moderationSettingsStore';
import { createOrgSyncStoreSlice, type OrgSyncStore } from '@/features/orgSync/orgSyncStore';
import { createRoomActionsStoreSlice, type RoomActionsStore } from '@/features/room/roomActionsStore';
import { createSiteConfigStoreSlice, type SiteConfigStore } from '@/features/siteConfig/siteConfigStore';
import { createToolsPageStoreSlice, type ToolsStore } from '@/features/tools/toolsStore';
import { createToSBannerStoreSlice, type ToSBannerStore } from '@/features/tosBanner/tosBannerStore';
import { createLayoutStoreSlice, type LayoutStore } from '@/layouts/store';
import type { CentralizedStore } from './zustand';

export type FullState = {
  AuthFormStoreData: AuthFormStore;
  AuthStoreData: AuthStore;
  StudentStoreData: StudentStore;
  AdminUsersStoreData: AdminUsersStore;
  AdminOrgsStoreData: AdminOrgsStore;
  JoinStoreData: JoinStore;
  ChatStoreData: ChatStore;
  OrgDashboardStoreData: OrgDashboardStore;
  OrgRoomsStoreData: OrgRoomsStore;
  OrgSyncStoreData: OrgSyncStore;
  RoomDetailsStoreData: RoomDetailsStore;
  RoomActionsStoreData: RoomActionsStore;
  NotificationStoreData: NotificationStore;
  ToSBannerStoreData: ToSBannerStore;
  LayoutStoreData: LayoutStore;
  RoomCoTeacherStoreData: RoomCoTeacherStore;
  ToolListStoreData: ToolListStore;
  EditToolStoreData: EditToolStore;
  SiteConfigStoreData: SiteConfigStore;
  ToolsStoreData: ToolsStore;
  TeacherProfileStoreData: TeacherProfileStore;
  ToolCustomizationStoreData: ToolCustomizationStore;
  ToolAccessStoreData: ToolAccessStore;
  ModerationSettingsStoreData: ModerationSettingsStore;
};

export type InitialStateProps = {
  studentData?: StudentUser;
  userData?: MagicSchoolUser;
  userAttributes?: Tables<'user_attributes'>[];
  orgAttributes?: FeatureAttribute[];
  userToolAccessOverrides?: Tables<'user_tool_access_overrides'>[];
  orgToolAccessOverrides?: Tables<'org_tool_access_overrides'>[];
  userTrials?: (Tables<'user_trials'> & { user_trial_reasons: { reason_code: string } })[];
  session?: SessionClaimsUser;
  featureFlags?: FeatureFlags;
  siteConfigs?: ClientSiteConfigs;
  isLTI?: boolean;
  lms?: string | null;
};

export const createInitialState = ({
  studentData,
  userData = emptyUser(),
  session,
  siteConfigs = defaultClientSiteConfigs,
  userAttributes,
  orgAttributes,
  userToolAccessOverrides,
  orgToolAccessOverrides,
  userTrials,
  isLTI,
  lms,
}: InitialStateProps) => {
  const authStore = createAuthStoreSlice({
    userData,
    session,
    userAttributes,
    orgAttributes,
    userToolAccessOverrides,
    orgToolAccessOverrides,
    userTrials,
    isLTI,
    lms: lms,
  });
  const siteConfigStore = createSiteConfigStoreSlice({ siteConfigs });
  const studentStore = createStudentStoreSlice({ studentData });
  const createState: CentralizedStore<FullState> = (setFull, getFull): FullState => ({
    AuthFormStoreData: createAuthFormStoreSlice({ setFull, getFull }),
    AuthStoreData: authStore({ setFull, getFull }),
    StudentStoreData: studentStore({ setFull, getFull }),
    AdminUsersStoreData: createAdminUsersStoreSlice({ setFull, getFull }),
    AdminOrgsStoreData: createAdminOrgsStoreSlice({ setFull, getFull }),
    JoinStoreData: createJoinStoreSlice({ setFull, getFull }),
    ChatStoreData: createChatStoreSlice({ setFull, getFull }),
    OrgDashboardStoreData: createOrgDashboardStoreSlice({ setFull, getFull }),
    OrgSyncStoreData: createOrgSyncStoreSlice({ setFull, getFull }),
    RoomDetailsStoreData: createRoomDetailsStoreSlice({ setFull, getFull }),
    NotificationStoreData: createNotificationStoreSlice({ setFull, getFull }),
    ToSBannerStoreData: createToSBannerStoreSlice({ setFull, getFull }),
    OrgRoomsStoreData: createOrgRoomsStoreSlice({ setFull, getFull }),
    LayoutStoreData: createLayoutStoreSlice({ setFull, getFull }),
    RoomActionsStoreData: createRoomActionsStoreSlice({ setFull, getFull }),
    RoomCoTeacherStoreData: createRoomJoinStoreSlice({ setFull, getFull }),
    ToolListStoreData: createToolListStoreSlice({ setFull, getFull }),
    EditToolStoreData: createEditToolStoreSlice({ setFull, getFull }),
    SiteConfigStoreData: siteConfigStore({ setFull, getFull }),
    ToolsStoreData: createToolsPageStoreSlice({ setFull, getFull }),
    TeacherProfileStoreData: createTeacherProfileStoreSlice({ setFull, getFull }),
    ToolCustomizationStoreData: createToolCustomizationStoreSlice({ setFull, getFull }),
    ToolAccessStoreData: createToolAccessStoreSlice({ setFull, getFull }),
    ModerationSettingsStoreData: createModerationSettingsStoreSlice({ setFull, getFull }),
  });
  return createState;
};
